import React, { useState, useEffect } from 'react'
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom'
import axios from 'axios'
import Navbar from './FrontOffice/components/Navbar'
import Footer from './FrontOffice/components/Footer'
import Home from './FrontOffice/home/Home'
import About from './FrontOffice/about/About'
import Contact from './FrontOffice/contact/Contact'
import References from './FrontOffice/references/References'
import Produits from './FrontOffice/produits/Produits'
import DetailProduit from './FrontOffice/produits/DetailProduit'
import Login from './auth/Login'
import LogoutCard from './auth/LogoutCard'
import Profile from './FrontOffice/profile/Profile'
import DemandeDevis from './FrontOffice/devis/DemandeDevisComplete'
import DashboardEntreprise from './FrontOffice/entrepriseDashboard/DashboardEntreprise'
import './App.css'
import Dashboard from './BackOffice/dashboard/Dashboard'
import DashboardClient from './BackOffice/dashboard/dashboardClient'
import DashboardVehicule from './BackOffice/dashboard/DashboardVehicule'
import DashboardEquipe from './BackOffice/dashboard/DashboardEquipe'
import ProduitsClientsDashboard from './BackOffice/dashboard/ProduitsClientsDashboard'
import BonsLivraisonDashboard from './BackOffice/dashboard/BonsLivraisonDashboard'
import BonLivraisonDetail from './BackOffice/dashboard/BonLivraisonDetail'
import ContactManagement from './BackOffice/contacts/Contact'
import LocationsManagement from './BackOffice/location/Locations'
import ProfileAdmin from './BackOffice/profileAdmin/ProfileAdmin'
import UsersManagement from './BackOffice/usersManagement/UsersManagement'
import ClientsManagement from './BackOffice/clients/ClientsManagement'
import DetailClient from './BackOffice/clients/DetailClient'
import DetailDossier from './BackOffice/clients/DetailDossier'
import ProspectsManagement from './BackOffice/prospects/ProspectsManagement'
import DetailProspect from './BackOffice/prospects/DetailProspect'
import CommerciauxManagement from './BackOffice/commerciaux/CommerciauxManagement'
import DetailCommercial from './BackOffice/commerciaux/DetailCommercial'
import CommercialCommissions from './BackOffice/commerciaux/CommercialCommissions'
import FournisseursManagement from './BackOffice/fournisseurs/FournisseursManagement'
import DetailFournisseur from './BackOffice/fournisseurs/DetailFournisseur'
import DemandesPrixManagement from './BackOffice/fournisseurs/DemandesPrixManagement'
import DetailDemandePrixPage from './BackOffice/fournisseurs/DetailDemandePrixPage'
import Region from './BackOffice/region/Region'
import HeroImages from './BackOffice/heroImages/HeroImages'
import EntreprisesManagement from './BackOffice/entreprises/EntreprisesManagement'
import ProduitsManagement from './BackOffice/produits/ProduitsManagement'
import PacksManagement from './BackOffice/produits/PacksManagement'
import DetailPack from './BackOffice/produits/DetailPack'
import ReferencesManagement from './BackOffice/references/ReferencesManagement'
import DemandesDevis from './BackOffice/demandesDevis/DemandesDevis'
import Reclamations from './BackOffice/reclamations/Reclamations'
import StockManagement from './BackOffice/stock/StockManagement'
import StockPalettes from './BackOffice/stock/StockPalettes'
import Inventaire from './BackOffice/stock/Inventaire'
import RapportMouvementsInventaire from './BackOffice/stock/RapportMouvementsInventaire'
import WarehouseVisualization3D from './BackOffice/warehouse/WarehouseVisualization3D'
import StegManagement from './BackOffice/steg/Steg'
import InstallateurManagement from './BackOffice/installateur/Installateur'
import EquipesInstallationManagement from './BackOffice/equipesInstallation/EquipesInstallationManagement'
import InstallateurDashboard from './BackOffice/installateur/InstallateurDashboard'
import CommercialDashboard from './BackOffice/commerciaux/CommercialDashboard'
import PermissionGuard from './BackOffice/components/PermissionGuard'
import GestionRH from './BackOffice/rh/GestionRH'
import DetailEmploye from './BackOffice/rh/employes/DetailEmploye'
import DetailSalaire from './BackOffice/rh/salaires/DetailSalaire'
import { getFirstAllowedBackOfficePage } from './utils/navigationUtils'
import { useSessionManager, clearSessionStorage } from './utils/useSessionManager'
import AuditLogs from './BackOffice/audit/AuditLogs'
import SensitiveDeleteConfig from './BackOffice/settings/SensitiveDeleteConfig'
import ArchivedItemsManager from './BackOffice/settings/ArchivedItemsManager'
import BackOfficeMenu from './BackOffice/components/BackOfficeMenu'
import { ProtectedPage } from './components/ProtectedSensitiveData'
import WelcomeAdmin from './BackOffice/components/WelcomeAdmin'
import CloturePeriode from './BackOffice/cloture/CloturePeriode'
import HistoriqueNotes from './BackOffice/clients/HistoriqueNotes'
import DashboardClientsMaster from './BackOffice/dashboard/DashboardClientsMaster'
import ActualitesNewsletter from './BackOffice/actualitesNewsletter/ActualitesNewsletter'
import MaintenancePage from './components/MaintenancePage'

// Activer/Désactiver le mode maintenance ici
const RAW_API_URL = import.meta.env.VITE_API_URL;
const NORMALIZED_API_URL = RAW_API_URL.replace(/\/+$/, '');
const API_BASE_URL = NORMALIZED_API_URL.endsWith('/api') ? NORMALIZED_API_URL : `${NORMALIZED_API_URL}/api`;
const MAINTENANCE_POLL_INTERVAL_MS = 30000;
const BACKOFFICE_USER_TYPES = new Set(['admin', 'moderateur']);
const BACKOFFICE_ALLOWED_ROLES = new Set(['admin', 'moderateur']);

const isBackofficeUserType = (type) => BACKOFFICE_USER_TYPES.has(type);

const isBackofficeRouteRoles = (roles = []) =>
  roles.some((role) => BACKOFFICE_ALLOWED_ROLES.has(role));

const getBackofficeLandingPath = (type) => {
  if (type === 'moderateur') return '/profileadmin';

  const perms = JSON.parse(localStorage.getItem('permissions') || '[]');
  const target = getFirstAllowedBackOfficePage(type, perms);
  return `/${target || 'dashboard'}`;
};

const fetchBackofficeMaintenanceStatus = async () => {
  try {
    const token = localStorage.getItem('token');
    const headers = { 'Cache-Control': 'no-cache' };
    if (token) headers.Authorization = `Bearer ${token}`;

    const response = await axios.get(`${API_BASE_URL}/maintenance/status`, { headers });
    const isMaintenance = response.data?.maintenance === true;
    const canBypass = response.data?.canBypassMaintenance === true;

    if (isMaintenance && canBypass) {
      return false;
    }

    return isMaintenance;
  } catch (error) {
    console.error('Erreur verification maintenance:', error);
    return false;
  }
};

// ─── Guard de route défini EN DEHORS du composant (stable, pas recréé à chaque render) ───
const RequireAuth = ({ children, allowedRoles, isAuthenticated, userType, maintenanceActive }) => {
  // Si le mode maintenance est actif et que l'utilisateur n'a pas de bypass, bloquer immédiatement l'accès
  if (maintenanceActive) {
    return <Navigate to="/maintenance" replace />
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />

  if (allowedRoles && !allowedRoles.includes(userType)) return <Navigate to="/" replace />
  return children
}

// ─── Pages BackOffice ───────────────────────────────────────────────────────
const BACK_OFFICE_PAGES = [
  'dashboard', 'dashboard-client', 'dashboard-vehicule', 'dashboard-equipe', 'dashboard-produits-clients', 'dashboard-bons-livraison', 'detail-bon-livraison', 'contacts', 'locations', 'profileadmin', 'usermanagements',
  'clientsmanagement', 'client-detail', 'detail-dossier', 'prospects',
  'commerciaux', 'commercial-detail', 'commercial-commissions', 'fournisseurs', 'fournisseur-detail',
  'demandes-prix', 'demande-prix-detail',
  'region', 'heroimages', 'entreprises', 'produitsAdmin', 'packsAdmin', 'pack-detail', 'referencesAdmin',
  'demandesdevis', 'reclamationsAdmin', 'stockmanagement', 'stockstatus',
  'inventaire', 'mouvements-inventaire', 'cloture-periode', 'warehouse3d', 'steg', 'installateur',
  'equipes-installation', 'installateur-dashboard', 'commercial-dashboard',
  'rh-employes', 'rh-employe-detail', 'rh-departements', 'rh-postes',
  'rh-demandes', 'rh-documents', 'rh-salaires', 'rh-salaire-detail',
  'rh-evaluations', 'rh-dashboard', 'gestion-rh', 'audit-logs', 'historique-notes',
  'dashboard-clients', 'deletion-settings', 'archived-items', 'actualites-newsletter'
]

// ─────────────────────────────────────────────────────────────────────────────
// Composant interne (a accès aux hooks React Router)
// ─────────────────────────────────────────────────────────────────────────────
function AppInner() {
  const navigate = useNavigate()
  const location = useLocation()

  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [userType, setUserType] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [maintenanceActive, setMaintenanceActive] = useState(false)

  // ── Helpers locaux (ne wrappent PAS de composant React, juste des fonctions) ─
  const getQueryParam = (key) => new URLSearchParams(location.search).get(key)

  const pathSegment = location.pathname.replace('/', '').split('/')[0].split('?')[0]
  const isBackOffice = BACK_OFFICE_PAGES.includes(pathSegment)
  const isEntreprisePage = pathSegment === 'entreprise-dashboard'
  const isMaintenancePage = pathSegment === 'maintenance'
  const hideStandardLayout = isBackOffice || isEntreprisePage || isMaintenancePage

  const syncMaintenanceNavigation = (activeMaintenance, type = userType, currentPath = location.pathname) => {
    setMaintenanceActive(activeMaintenance)

    if (activeMaintenance && currentPath !== '/maintenance' && currentPath !== '/login') {
      navigate('/maintenance', { replace: true })
      return
    }

    if (!activeMaintenance && currentPath === '/maintenance') {
      navigate(getBackofficeLandingPath(type || 'admin'), { replace: true })
    }
  }

  // ── Navigation ─────────────────────────────────────────────────────────────
  // params objet → query string : /page?key=val&key2=val2
  // params string → /page?id=val  (évite les routes non définies comme /page/val)
  const handleNavigation = (page, params = null) => {
    if (!page) return
    if (params && typeof params === 'object') {
      const qs = new URLSearchParams(params).toString()
      navigate(`/${page}?${qs}`)
    } else if (params) {
      navigate(`/${page}?id=${encodeURIComponent(params)}`)
    } else {
      navigate(`/${page}`)
    }
  }

  // ── Auth & Gestionnaire de Session (10 min inactivité) ───
  useSessionManager(isAuthenticated, () => {
    setIsAuthenticated(false)
    setUserType(null)
    setMaintenanceActive(false)
    clearSessionStorage()
    navigate('/login')
  })

  useEffect(() => {
    let mounted = true

    const initializeSession = async () => {
      const token = localStorage.getItem('token')
      const type = localStorage.getItem('typeUser')

      const activeMaintenance = await fetchBackofficeMaintenanceStatus()

      if (!mounted) return

      if (token) {
        setIsAuthenticated(true)
        setUserType(type)
      }

      syncMaintenanceNavigation(activeMaintenance, type)

      setIsLoading(false)
    }

    initializeSession()

    return () => {
      mounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleLoginSuccess = async (type) => {
    setIsAuthenticated(true)
    setUserType(type)

    const activeMaintenance = await fetchBackofficeMaintenanceStatus()
    setMaintenanceActive(activeMaintenance)

    if (activeMaintenance) {
      navigate('/maintenance', { replace: true })
      return
    }

    if (type === 'admin' || type === 'moderateur') {
      const perms = JSON.parse(localStorage.getItem('permissions') || '[]')
      const target = type === 'moderateur'
        ? 'profileadmin'
        : getFirstAllowedBackOfficePage(type, perms)

      // Navigation vers la card de bienvenue avant le Dashboard
      navigate('/welcome-admin', { state: { target: `/${target || 'dashboard'}` } })
    } else if (type === 'entreprise') {
      navigate('/entreprise-dashboard')
    } else if (type === 'installateur') {
      navigate('/installateur-dashboard')
    } else if (type === 'commercial') {
      navigate('/commercial-dashboard')
    } else {
      navigate('/profile')
    }
  }

  const handleLogout = async () => {
    // 1. On navigue d'abord vers la page de sortie pour un retour visuel immédiat
    navigate('/goodbye')

    try {
      const token = localStorage.getItem('token');
      const refreshToken = localStorage.getItem('refreshToken');

      // Appel au backend pour clôturer la session proprement dans l'Audit Trail
      if (token) {
        await axios.post(`${API_BASE_URL}/auth/logout`, { refreshToken }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (err) {
      console.error('Erreur lors de la déconnexion backend:', err);
    }

    // 2. On laisse un court délai pour que la navigation s'enclenche 
    // avant de vider le state (évite que le RequireAuth de la page actuelle ne redirige vers /login)
    setTimeout(() => {
      setIsAuthenticated(false)
      setUserType(null)
      setMaintenanceActive(false)
      clearSessionStorage()
    }, 100)
  }

  // ── Chargement ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated || !isBackofficeUserType(userType)) return undefined

    let cancelled = false

    const checkMaintenance = async () => {
      const activeMaintenance = await fetchBackofficeMaintenanceStatus()
      if (!cancelled) {
        syncMaintenanceNavigation(activeMaintenance, userType)
      }
    }

    checkMaintenance()
    const intervalId = window.setInterval(checkMaintenance, MAINTENANCE_POLL_INTERVAL_MS)

    return () => {
      cancelled = true
      window.clearInterval(intervalId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, userType, location.pathname])

  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const response = error?.response
        const isMaintenanceError =
          response?.status === 503 &&
          response?.data?.code === 'BACKOFFICE_MAINTENANCE'

        if (isMaintenanceError && isBackofficeUserType(localStorage.getItem('typeUser'))) {
          syncMaintenanceNavigation(true, localStorage.getItem('typeUser'))
        }

        return Promise.reject(error)
      }
    )

    return () => {
      axios.interceptors.response.eject(interceptorId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  if (isLoading) {
    return (
      <div className="App">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'var(--light-gray)' }}>
          <div>Chargement...</div>
        </div>
      </div>
    )
  }

  // ── Helpers JSX (wrappers courts) ──────────────────────────────────────────
  // RA: RequireAuth avec l'état courant injecté
  const RA = ({ children, allowedRoles }) => (
    <RequireAuth
      isAuthenticated={isAuthenticated}
      userType={userType}
      allowedRoles={allowedRoles}
      maintenanceActive={maintenanceActive}
    >
      {children}
    </RequireAuth>
  )
  // PG: PermissionGuard avec le contexte courant
  const PG = ({ module, children }) => (
    <PermissionGuard module={module} onNavigate={handleNavigation} onLogout={handleLogout} currentPage={pathSegment}>
      {children}
    </PermissionGuard>
  )

  return (
    <div className="App">
      {!hideStandardLayout && (
        <Navbar
          onNavigate={handleNavigation}
          isAuthenticated={isAuthenticated}
          onLogout={handleLogout}
          userTypeUser={userType}
        />
      )}
      <main>
        <Routes>

          {/* ─── FRONT OFFICE ──────────────────────────────────────────── */}
          <Route path="/" element={<Home onNavigate={handleNavigation} isAuthenticated={isAuthenticated} />} />
          <Route path="/home" element={<Navigate to="/" replace />} />
          <Route path="/about" element={<About />} />
          <Route path="/references" element={<References onNavigate={handleNavigation} />} />
          <Route path="/produits" element={<Produits userType={userType} isAuthenticated={isAuthenticated} />} />
          <Route path="/produits/:category" element={<Produits userType={userType} isAuthenticated={isAuthenticated} />} />
          <Route path="/produit/:id" element={<DetailProduit userType={userType} isAuthenticated={isAuthenticated} />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/devis" element={<DemandeDevis onNavigate={handleNavigation} />} />
          <Route path="/login" element={<Login onLoginSuccess={handleLoginSuccess} />} />
          <Route path="/goodbye" element={<LogoutCard />} />
          <Route path="/profile" element={<RA><Profile onLogout={handleLogout} /></RA>} />
          <Route path="/entreprise-dashboard" element={
            <RA allowedRoles={['entreprise']}>
              <DashboardEntreprise onLogout={handleLogout} />
            </RA>
          } />

          {/* ─── BACK OFFICE ───────────────────────────────────────────── */}
          <Route path="/welcome-admin" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <WelcomeAdmin />
            </RA>
          } />
          <Route path="/dashboard" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <PG module="dashboard"><Dashboard onNavigate={handleNavigation} onLogout={handleLogout} currentPage="dashboard" /></PG>
            </RA>
          } />
          <Route path="/dashboard-client" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <PG module="dashboard"><DashboardClient onNavigate={handleNavigation} onLogout={handleLogout} currentPage="dashboard-client" /></PG>
            </RA>
          } />
          <Route path="/dashboard-clients" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <PG module="dashboard"><DashboardClientsMaster onNavigate={handleNavigation} onLogout={handleLogout} currentPage="dashboard-clients" /></PG>
            </RA>
          } />

          <Route path="/dashboard-vehicule" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <PG module="dashboard"><DashboardVehicule onNavigate={handleNavigation} onLogout={handleLogout} currentPage="dashboard-vehicule" /></PG>
            </RA>
          } />
          <Route path="/dashboard-equipe" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <PG module="dashboard"><DashboardEquipe onNavigate={handleNavigation} onLogout={handleLogout} currentPage="dashboard-equipe" /></PG>
            </RA>
          } />
          <Route path="/dashboard-produits-clients" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <PG module="dashboard"><ProduitsClientsDashboard onNavigate={handleNavigation} onLogout={handleLogout} currentPage="dashboard-produits-clients" /></PG>
            </RA>
          } />
          <Route path="/dashboard-bons-livraison" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <PG module="documents"><BonsLivraisonDashboard onNavigate={handleNavigation} onLogout={handleLogout} currentPage="dashboard-bons-livraison" /></PG>
            </RA>
          } />
          <Route path="/detail-bon-livraison/:blId" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <PG module="documents"><BonLivraisonDetail onNavigate={handleNavigation} onLogout={handleLogout} currentPage="dashboard-bons-livraison" /></PG>
            </RA>
          } />
          <Route path="/usermanagements" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <PG module="utilisateurs">
                <ProtectedPage
                  title="Gestion des Utilisateurs protégée"
                  onNavigate={handleNavigation}
                  onLogout={handleLogout}
                  currentPage="usermanagements"
                >
                  <UsersManagement onNavigate={handleNavigation} onLogout={handleLogout} currentPage="usermanagements" />
                </ProtectedPage>
              </PG>
            </RA>
          } />
          <Route path="/clientsmanagement" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <PG module="clients"><ClientsManagement onNavigate={handleNavigation} onLogout={handleLogout} currentPage="clientsmanagement" /></PG>
            </RA>
          } />
          <Route path="/historique-notes" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <PG module="clients">
                <HistoriqueNotes onNavigate={handleNavigation} onLogout={handleLogout} currentPage="historique-notes" />
              </PG>
            </RA>
          } />
          <Route path="/client-detail" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <PG module="clients">
                <DetailClient
                  clientId={getQueryParam('clientId') || getQueryParam('id')}
                  onNavigate={handleNavigation}
                  onLogout={handleLogout}
                  currentPage="client-detail"
                />
              </PG>
            </RA>
          } />
          <Route path="/detail-dossier" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <PG module="dossiers">
                <DetailDossier
                  clientId={getQueryParam('clientId') || getQueryParam('id')}
                  dossierId={getQueryParam('dossierId')}
                  onNavigate={handleNavigation}
                  onLogout={handleLogout}
                  currentPage="detail-dossier"
                />
              </PG>
            </RA>
          } />
          <Route path="/prospects" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <PG module="prospects"><ProspectsManagement onNavigate={handleNavigation} onLogout={handleLogout} currentPage="prospects" /></PG>
            </RA>
          } />
          <Route path="/prospects/:id" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <PG module="prospects"><DetailProspect onNavigate={handleNavigation} onLogout={handleLogout} currentPage="prospects" /></PG>
            </RA>
          } />
          <Route path="/commerciaux" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <PG module="commerciaux"><CommerciauxManagement onNavigate={handleNavigation} onLogout={handleLogout} currentPage="commerciaux" /></PG>
            </RA>
          } />
          <Route path="/commercial-detail" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <PG module="commerciaux">
                <DetailCommercial
                  commercialId={getQueryParam('commercialId') || getQueryParam('id')}
                  onNavigate={handleNavigation}
                  currentPage="commercial-detail"
                />
              </PG>
            </RA>
          } />
          <Route path="/commercial-commissions" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <PG module="commerciaux">
                <CommercialCommissions onNavigate={handleNavigation} currentPage="commercial-commissions" />
              </PG>
            </RA>
          } />
          <Route path="/fournisseurs" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <PG module="fournisseurs"><FournisseursManagement onNavigate={handleNavigation} onLogout={handleLogout} currentPage="fournisseurs" /></PG>
            </RA>
          } />
          <Route path="/fournisseur-detail" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <PG module="fournisseurs">
                <DetailFournisseur
                  fournisseurId={getQueryParam('fournisseurId') || getQueryParam('id')}
                  dashboardAction={getQueryParam('action')}
                  sourceBlId={getQueryParam('blId')}
                  sourceBcId={getQueryParam('bcId')}
                  onNavigate={handleNavigation}
                  currentPage="fournisseur-detail"
                />
              </PG>
            </RA>
          } />
          <Route path="/demandes-prix" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <PG module="fournisseurs"><DemandesPrixManagement onNavigate={handleNavigation} onLogout={handleLogout} currentPage="demandes-prix" /></PG>
            </RA>
          } />
          <Route path="/demande-prix-detail" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <PG module="fournisseurs"><DetailDemandePrixPage onNavigate={handleNavigation} onLogout={handleLogout} currentPage="demandes-prix" /></PG>
            </RA>
          } />
          <Route path="/entreprises" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <PG module="utilisateurs"><EntreprisesManagement onNavigate={handleNavigation} onLogout={handleLogout} currentPage="entreprises" /></PG>
            </RA>
          } />
          <Route path="/produitsAdmin" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <PG module="produits"><ProduitsManagement onNavigate={handleNavigation} onLogout={handleLogout} currentPage="produitsAdmin" /></PG>
            </RA>
          } />
          <Route path="/packsAdmin" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <PG module="produits"><PacksManagement onNavigate={handleNavigation} onLogout={handleLogout} currentPage="packsAdmin" /></PG>
            </RA>
          } />
          <Route path="/pack-detail" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <PG module="produits">
                <DetailPack
                  packId={getQueryParam('packId') || getQueryParam('id')}
                  onNavigate={handleNavigation}
                  onLogout={handleLogout}
                  currentPage="pack-detail"
                />
              </PG>
            </RA>
          } />
          <Route path="/referencesAdmin" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <PG module="references"><ReferencesManagement onNavigate={handleNavigation} onLogout={handleLogout} currentPage="referencesAdmin" /></PG>
            </RA>
          } />
          <Route path="/demandesdevis" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <PG module="devis"><DemandesDevis onNavigate={handleNavigation} onLogout={handleLogout} currentPage="demandesdevis" /></PG>
            </RA>
          } />
          <Route path="/reclamationsAdmin" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <PG module="reclamations"><Reclamations onNavigate={handleNavigation} onLogout={handleLogout} currentPage="reclamationsAdmin" /></PG>
            </RA>
          } />
          <Route path="/stockmanagement" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <PG module="stock"><StockManagement onNavigate={handleNavigation} onLogout={handleLogout} currentPage="stockmanagement" /></PG>
            </RA>
          } />
          <Route path="/stockstatus" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <PG module="stock"><StockPalettes onNavigate={handleNavigation} onLogout={handleLogout} currentPage="stockstatus" /></PG>
            </RA>
          } />
          <Route path="/inventaire" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <PG module="stock"><Inventaire onNavigate={handleNavigation} onLogout={handleLogout} currentPage="inventaire" /></PG>
            </RA>
          } />
          <Route path="/mouvements-inventaire" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <PG module="stock"><RapportMouvementsInventaire onNavigate={handleNavigation} onLogout={handleLogout} currentPage="mouvements-inventaire" /></PG>
            </RA>
          } />
          <Route path="/cloture-periode" element={
            <RA allowedRoles={['admin']}>
              <CloturePeriode onNavigate={handleNavigation} onLogout={handleLogout} currentPage="cloture-periode" />
            </RA>
          } />
          <Route path="/warehouse3d" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <PG module="stock"><WarehouseVisualization3D onNavigate={handleNavigation} onLogout={handleLogout} currentPage="warehouse3d" /></PG>
            </RA>
          } />
          <Route path="/region" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <PG module="dashboard"><Region onNavigate={handleNavigation} onLogout={handleLogout} currentPage="region" /></PG>
            </RA>
          } />
          <Route path="/heroimages" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <PG module="dashboard"><HeroImages onNavigate={handleNavigation} onLogout={handleLogout} currentPage="heroimages" /></PG>
            </RA>
          } />
          <Route path="/steg" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <PG module="clients"><StegManagement onNavigate={handleNavigation} onLogout={handleLogout} currentPage="steg" /></PG>
            </RA>
          } />
          <Route path="/installateur" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <PG module="installateurs"><InstallateurManagement onNavigate={handleNavigation} onLogout={handleLogout} currentPage="installateur" /></PG>
            </RA>
          } />
          <Route path="/equipes-installation" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <PG module="equipes_installation"><EquipesInstallationManagement onNavigate={handleNavigation} onLogout={handleLogout} currentPage="equipes-installation" /></PG>
            </RA>
          } />
          <Route path="/locations" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <PG module="partenaires"><LocationsManagement onNavigate={handleNavigation} onLogout={handleLogout} currentPage="locations" /></PG>
            </RA>
          } />
          <Route path="/contacts" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <PG module="utilisateurs"><ContactManagement onNavigate={handleNavigation} onLogout={handleLogout} currentPage="contacts" /></PG>
            </RA>
          } />
          <Route path="/profileadmin" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <ProfileAdmin onNavigate={handleNavigation} onLogout={handleLogout} currentPage="profileadmin" />
            </RA>
          } />
          <Route path="/installateur-dashboard" element={
            <RA allowedRoles={['installateur']}>
              <InstallateurDashboard onLogout={handleLogout} />
            </RA>
          } />
          <Route path="/commercial-dashboard" element={
            <RA allowedRoles={['commercial']}>
              <CommercialDashboard onLogout={handleLogout} />
            </RA>
          } />

          {/* ─── Actualités & Newsletter ────────────────────────── */}
          <Route path="/actualites-newsletter" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <ActualitesNewsletter onNavigate={handleNavigation} onLogout={handleLogout} currentPage="actualites-newsletter" />
            </RA>
          } />

          {/* ─── RH ───────────────────────────────────────────── */}
          <Route path="/gestion-rh" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <PG module="rh"><GestionRH onNavigate={handleNavigation} onLogout={handleLogout} currentPage="gestion-rh" /></PG>
            </RA>
          } />

          {/* Redirections legacy vers le nouveau GestionRH */}
          <Route path="/rh-dashboard" element={<Navigate to="/gestion-rh?tab=dashboard" replace />} />
          <Route path="/rh-employes" element={<Navigate to="/gestion-rh?tab=employes" replace />} />
          <Route path="/rh-departements" element={<Navigate to="/gestion-rh?tab=departements" replace />} />
          <Route path="/rh-postes" element={<Navigate to="/gestion-rh?tab=postes" replace />} />
          <Route path="/rh-demandes" element={<Navigate to="/gestion-rh?tab=conges" replace />} />
          <Route path="/rh-documents" element={<Navigate to="/gestion-rh?tab=documents-rh" replace />} />
          <Route path="/rh-salaires" element={<Navigate to="/gestion-rh?tab=paie" replace />} />
          <Route path="/rh-evaluations" element={<Navigate to="/gestion-rh?tab=performances" replace />} />

          <Route path="/rh-employe-detail" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <PG module="rh">
                <DetailEmploye
                  employeId={getQueryParam('id')}
                  onNavigate={handleNavigation}
                  onLogout={handleLogout}
                  currentPage="rh-employe-detail"
                />
              </PG>
            </RA>
          } />

          <Route path="/rh-salaire-detail" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <PG module="rh">
                <DetailSalaire
                  salaireId={getQueryParam('id')}
                  onNavigate={handleNavigation}
                  onLogout={handleLogout}
                  currentPage="rh-salaire-detail"
                />
              </PG>
            </RA>
          } />

          <Route path="/maintenance" element={<MaintenancePage onLogout={handleLogout} />} />

          {/* ─── Audit Trail (admin & modérateur avec autorisation dérogatoire) ───────────────────── */}
          <Route path="/audit-logs" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <ProtectedPage
                title="Audit Trail & Logs Système protégés"
                onNavigate={handleNavigation}
                onLogout={handleLogout}
                currentPage="audit-logs"
                actionCode="AUDIT_LOGS_VIEW"
              >
                <AuditLogs onNavigate={handleNavigation} onLogout={handleLogout} currentPage="audit-logs" />
              </ProtectedPage>
            </RA>
          } />

          {/* ─── Gestion des Actions & Données Sensibles (Admin & Modérateur) ──────────────── */}
          <Route path="/deletion-settings" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <ProtectedPage
                title="Gestion des Actions & Données Sensibles"
                message="Veuillez saisir le code secret de protection pour accéder au centre de gouvernance et de sécurité des actions."
                onNavigate={handleNavigation}
                onLogout={handleLogout}
                currentPage="deletion-settings"
                actionCode="SETTINGS_UPDATE"
              >
                <div className="backoffice-page-content" style={{ padding: '20px' }}>
                  <SensitiveDeleteConfig />
                </div>
              </ProtectedPage>
            </RA>
          } />

          {/* ─── Éléments Archivés (Admin & Modérateur) ───────────────────── */}
          <Route path="/archived-items" element={
            <RA allowedRoles={['admin', 'moderateur']}>
              <ProtectedPage
                title="Éléments Supprimés & Archivés"
                message="Veuillez saisir le code secret de protection pour consulter ou restaurer les données archivées."
                onNavigate={handleNavigation}
                onLogout={handleLogout}
                currentPage="archived-items"
                actionCode="ARCHIVE_RESTORE"
              >
                <div className="backoffice-page-content" style={{ padding: '20px' }}>
                  <ArchivedItemsManager />
                </div>
              </ProtectedPage>
            </RA>
          } />

          {/* Fallback — toute URL inconnue → accueil */}
          <Route path="*" element={<Navigate to="/" replace />} />

        </Routes>
      </main>
      {!hideStandardLayout && <Footer />}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Composant racine avec BrowserRouter
// ─────────────────────────────────────────────────────────────────────────────
function App() {
  return (
    <BrowserRouter>
      <AppInner />
    </BrowserRouter>
  )
}

export default App
